import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';
import {
  Clock, CheckCircle2, ChefHat, PackageCheck, PartyPopper,
  PauseCircle, XCircle, AlertTriangle, CreditCard, StickyNote,
  Store, Smartphone, Wallet, Megaphone, Coffee, ChevronRight,
  Leaf, Sparkles,
} from 'lucide-react';
import { host, formatDateTime } from './script/variables';
import { load } from '@cashfreepayments/cashfree-js';
import { generateInvoice } from './GenerateInvoice';
import './style/orderStatus.css';
import { Link } from 'react-router-dom';
/* ─────────────────────────────────────────────────────────────
   LOTTIE ASSETS
   These are referenced by path (not bundled), so they're fetched
   at runtime the same way an image would be. Download a matching
   animation for each key below from a source such as lottiefiles.com
   and place the exported JSON at the path shown. If a file is
   missing or fails to load, the UI falls back to a static Lucide
   icon automatically — nothing breaks in production either way.

   Suggested search terms on lottiefiles.com are in the comments.
───────────────────────────────────────────────────────────── */
const LOTTIE_BASE = '/lottie';
const LOTTIE = {
  Pending:   `${LOTTIE_BASE}/order-placed.json`,     // search: "order placed / receipt"
  Accepted:  `${LOTTIE_BASE}/order-accepted.json`,   // search: "checkmark confirm"
  Preparing: `${LOTTIE_BASE}/order-preparing.json`,  // search: "chef cooking pot"
  Ready:     `${LOTTIE_BASE}/order-ready.json`,      // search: "food ready bag"
  Completed: `${LOTTIE_BASE}/order-completed.json`,  // search: "celebration confetti"
  Hold:      `${LOTTIE_BASE}/order-hold.json`,       // search: "pause clock"
  Cancelled: `${LOTTIE_BASE}/order-cancelled.json`,  // search: "cancel cross"
  Rejected:  `${LOTTIE_BASE}/order-rejected.json`,   // search: "alert warning"
  waiting:   `${LOTTIE_BASE}/waiting-coffee.json`,   // search: "coffee cup steam loop"
};

/* ─── Sound ─────────────────────────────────────────────── */
const playStatusSound = (type = 'delivery') => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = (t, freq, dur, vol = 0.3) => {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.start(t); osc.stop(t + dur);
    };
    const now = ctx.currentTime;
    if (type === 'payment') {
      beep(now,        523, 0.15, 0.35);
      beep(now + 0.15, 659, 0.15, 0.35);
      beep(now + 0.30, 784, 0.25, 0.4);
    } else {
      beep(now,        660, 0.18, 0.3);
      beep(now + 0.2,  880, 0.22, 0.25);
    }
  } catch { /* silent fail */ }
};

/* ─── Config ─────────────────────────────────────────────── */

// The happy-path pipeline shown in the stepper
const DELIVERY_STEPS = ['Pending', 'Accepted', 'Preparing', 'Ready', 'Completed'];

const DELIVERY_META = {
  Pending: {
    Icon: Clock, lottie: LOTTIE.Pending, label: 'Order Placed',
    color: '#e8a135', bg: '#fef3df', borderColor: 'rgba(232,161,53,.25)',
    desc: 'Your order has been placed — waiting for confirmation',
  },
  Accepted: {
    Icon: CheckCircle2, lottie: LOTTIE.Accepted, label: 'Order Accepted',
    color: '#10b981', bg: '#d1fae5', borderColor: 'rgba(16,185,129,.25)',
    desc: 'The restaurant has accepted your order',
  },
  Preparing: {
    Icon: ChefHat, lottie: LOTTIE.Preparing, label: 'Being Prepared',
    color: '#3b82f6', bg: '#dbeafe', borderColor: 'rgba(59,130,246,.25)',
    desc: 'The kitchen is cooking your food',
  },
  Ready: {
    Icon: PackageCheck, lottie: LOTTIE.Ready, label: 'Ready to Pickup',
    color: '#2d7a4f', bg: '#e6f4ec', borderColor: 'rgba(45,122,79,.25)',
    desc: 'Your order is ready. Please collect it',
  },
  Completed: {
    Icon: PartyPopper, lottie: LOTTIE.Completed, label: 'Order Completed',
    color: '#c0472b', bg: '#f5e8e4', borderColor: 'rgba(192,71,43,.25)',
    desc: 'Thank you. Enjoy your meal',
  },
  // ── Off-path states (no stepper shown) ──
  Hold: {
    Icon: PauseCircle, lottie: LOTTIE.Hold, label: 'Order On Hold',
    color: '#8b5cf6', bg: '#ede9fe', borderColor: 'rgba(139,92,246,.25)',
    desc: 'Your order has been put on hold — we\'ll resume shortly',
  },
  Cancelled: {
    Icon: XCircle, lottie: LOTTIE.Cancelled, label: 'Order Cancelled',
    color: '#c0472b', bg: '#fef2f2', borderColor: 'rgba(192,71,43,.2)',
    desc: 'This order was cancelled',
  },
  Rejected: {
    Icon: AlertTriangle, lottie: LOTTIE.Rejected, label: 'Order Rejected',
    color: '#ef4444', bg: '#fee2e2', borderColor: 'rgba(239,68,68,.2)',
    desc: 'The restaurant could not accept this order. Please contact them for assistance.',
  },
};

const PAYMENT_META = {
  Pending:   { Icon: CreditCard,   label: 'Payment Pending', color: '#e8a135', bg: '#fef3df', border: 'rgba(232,161,53,.25)' },
  Completed: { Icon: CheckCircle2, label: 'Payment Done',    color: '#2d7a4f', bg: '#e6f4ec', border: 'rgba(45,122,79,.25)'  },
  Due:       { Icon: Wallet,       label: 'Payment Due',     color: '#6366f1', bg: '#ede9fe', border: 'rgba(99,102,241,.25)' },
};

// Terminal states where no further action is expected
const TERMINAL_STATUSES  = ['Completed', 'Cancelled', 'Rejected'];
// Off-pipeline states that skip the stepper
const OFF_PATH_STATUSES  = ['Hold', 'Cancelled', 'Rejected'];

/* ─── Helpers ─────────────────────────────────────────────── */
const safeStr = (v, fb = '') => (v == null || v === '' ? fb : String(v));
const safeNum = (v, fb = 0)  => (!isNaN(Number(v)) ? Number(v) : fb);

/* ═══════════════════════════════════════
   STATUS ICON
   Plays a small Lottie loop when a JSON file is present at the
   given path; silently falls back to a static Lucide icon if the
   file is missing or fails to load, so this is always safe to ship.
═══════════════════════════════════════ */
const StatusIcon = ({ src, FallbackIcon, size = 28, color }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return FallbackIcon ? <FallbackIcon size={size} color={color} strokeWidth={2.1} /> : null;
  }

  return (
    <Player
      src={src}
      autoplay
      loop
      keepLastFrame
      style={{ width: size, height: size }}
      onEvent={(event) => { if (event === 'error') setFailed(true); }}
    />
  );
};

/* ═══════════════════════════════════════
   OFF-PATH BANNER
   Shown instead of stepper for Hold,
   Cancelled, Rejected
═══════════════════════════════════════ */
const OffPathBanner = ({ deliveryStatus }) => {
  const meta = DELIVERY_META[deliveryStatus];
  if (!meta) return null;

  return (
    <div
      className="os-offpath-banner"
      style={{ background: meta.bg, borderColor: meta.borderColor }}
    >
      <span className="os-offpath-icon">
        <StatusIcon src={meta.lottie} FallbackIcon={meta.Icon} size={30} color={meta.color} />
      </span>
      <div>
        <p className="os-offpath-title" style={{ color: meta.color }}>{meta.label}</p>
        <p className="os-offpath-sub">{meta.desc}</p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════
   STATUS STEPPER
═══════════════════════════════════════ */
const StatusStepper = ({ deliveryStatus }) => {
  // For off-path statuses, don't show the linear stepper
  if (OFF_PATH_STATUSES.includes(deliveryStatus)) {
    return <OffPathBanner deliveryStatus={deliveryStatus} />;
  }

  const currentIdx = DELIVERY_STEPS.indexOf(deliveryStatus);

  return (
    <div className="os-stepper">
      {DELIVERY_STEPS.map((step, idx) => {
        const meta       = DELIVERY_META[step];
        const done       = idx < currentIdx;
        const active     = idx === currentIdx;
        const isLast     = idx === DELIVERY_STEPS.length - 1;
        const stateClass = done ? 'done' : active ? 'active' : '';
        const DormantIcon = meta.Icon;

        return (
          <React.Fragment key={step}>
            <div className="os-step-col">
              <div className="os-step-circle-wrap">
                {active && (
                  <span className="os-step-pulse" style={{ background: meta.color + '28' }} />
                )}
                <div
                  className={`os-step-circle ${stateClass}`}
                  data-step={step}
                  style={active ? { borderColor: meta.color, background: meta.bg, color: meta.color } : {}}
                >
                  {done ? (
                    <CheckCircle2 size={17} strokeWidth={2.2} />
                  ) : active ? (
                    <StatusIcon src={meta.lottie} FallbackIcon={meta.Icon} size={22} color={meta.color} />
                  ) : (
                    <DormantIcon size={17} strokeWidth={2} />
                  )}
                </div>
              </div>
              <p
                className={`os-step-label ${stateClass}`}
                style={active ? { color: meta.color } : {}}
              >
                {meta.label}
              </p>
            </div>
            {!isLast && <div className={`os-step-line ${done ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════
   STATUS FLASH
═══════════════════════════════════════ */
const StatusFlash = ({ deliveryStatus, animKey }) => {
  const meta = DELIVERY_META[deliveryStatus] || DELIVERY_META.Pending;
  return (
    <div
      key={animKey}
      className="os-status-flash"
      style={{ background: meta.bg, borderColor: meta.borderColor }}
    >
      <span className="os-flash-icon">
        <StatusIcon src={meta.lottie} FallbackIcon={meta.Icon} size={34} color={meta.color} />
      </span>
      <div>
        <p className="os-flash-title" style={{ color: meta.color }}>{meta.label}</p>
        <p className="os-flash-desc">{meta.desc}</p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════
   AD SLOT — "While You Wait"
   A rotating, clearly-labelled promotional slot shown only while
   the order is still in progress. Feed it real promotions via
   `ads` (e.g. order.cafe?.promotions from your API); it falls back
   to a sensible default set otherwise.
═══════════════════════════════════════ */
const DEFAULT_ADS = [
  {
    id: 'dessert-upsell',
    title: 'Something sweet to finish?',
    body: 'Add a dessert to your next order and save 10%.',
    cta: 'Browse Desserts',
    href: '',
  },
  {
    id: 'referral',
    title: 'Bring a friend next time',
    body: 'They get ₹50 off their first order, you get ₹50 credit.',
    cta: 'Share Invite',
    href: '',
  },
  {
    id: 'loyalty',
    title: 'Earn points on this order',
    body: 'Join the rewards program and start collecting points today.',
    cta: 'Join Rewards',
    href: '',
  },
];

const AD_ROTATE_MS = 6000;

const AdSlot = ({ ads }) => {
  const list = ads?.length ? ads : DEFAULT_ADS;
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setIndex(0);
    if (list.length <= 1) return undefined;
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % list.length);
    }, AD_ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [list]);

  if (!list.length) return null;
  const ad = list[index];

  return (
    <div className="os-card os-ad-card">
      <div className="os-ad-hd">
        <p className="os-card-label os-ad-label">
          <Megaphone size={11} />
          While You Wait
        </p>
        <span className="os-ad-tag">Sponsored</span>
      </div>

      <div className="os-ad-body" key={ad.id}>
        <div className="os-ad-anim">
          <StatusIcon src={LOTTIE.waiting} FallbackIcon={Coffee} size={38} color="var(--amber)" />
        </div>
        <div className="os-ad-copy">
          <p className="os-ad-title">{ad.title}</p>
          <p className="os-ad-desc">{ad.body}</p>
          {ad.href && (
            <a className="os-ad-cta" href={ad.href}>
              {ad.cta}
              <ChevronRight size={13} />
            </a>
          )}
        </div>
      </div>

      {list.length > 1 && (
        <div className="os-ad-dots">
          {list.map((a, i) => (
            <span key={a.id} className={`os-ad-dot ${i === index ? 'active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   INVOICE BUTTON
═══════════════════════════════════════ */
const InvoiceDownloadBtn = ({ order }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try { await generateInvoice(order); }
    finally { setBusy(false); }
  };

  return (
    <button
      className="os-invoice-btn"
      onClick={handleClick}
      disabled={busy}
      aria-label="Download invoice PDF"
    >
      {busy ? (
        <><span className="os-btn-spinner" />Generating PDF…</>
      ) : (
        <>
          <svg viewBox="0 0 20 20" fill="none" width="15" height="15" aria-hidden="true">
            <path d="M10 3v9m0 0l-3-3m3 3l3-3"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 14v2a1 1 0 001 1h12a1 1 0 001-1v-2"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          Download Invoice (PDF)
        </>
      )}
    </button>
  );
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const OrderStatus = ({ socket }) => {
  const { oid } = useParams();

  const [order,          setOrder]          = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState('Pending');
  const [paymentStatus,  setPaymentStatus]  = useState('Pending');
  const [restaurantId,   setRestaurantId]   = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [payLoading,     setPayLoading]     = useState(false);
  const [flashKey,       setFlashKey]       = useState(0);

  const prevDeliveryRef = useRef(null);
  const prevPaymentRef  = useRef(null);
  const pollRef         = useRef(null);
  const cashfreeRef     = useRef(null);

  useEffect(() => {
    load({ mode: 'production' }).then(cf => { cashfreeRef.current = cf; });
  }, []);

  const fetchOrderDetails = useCallback(async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${host}/order/fetchOrderDetails`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ orderId: oid }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const o    = data[0];
      if (!o) return;

      const newDelivery = safeStr(o.deliveryStatus, 'Pending');
      const newPayment  = safeStr(o.paymentStatus,  'Pending');

      if (silent) {
        if (prevDeliveryRef.current && prevDeliveryRef.current !== newDelivery) {
          playStatusSound('delivery');
          setFlashKey(k => k + 1);
        }
        if (prevPaymentRef.current && prevPaymentRef.current !== newPayment) {
          playStatusSound('payment');
        }
      }

      prevDeliveryRef.current = newDelivery;
      prevPaymentRef.current  = newPayment;

      setDeliveryStatus(newDelivery);
      setPaymentStatus(newPayment);
      setOrder(o);
      setRestaurantId(o.restaurantId);

      // Stop polling for fully terminal orders
      if (TERMINAL_STATUSES.includes(newDelivery) && newPayment !== 'Pending') {
        clearInterval(pollRef.current);
      }
    } catch (err) {
      console.error('fetchOrderDetails:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [oid]);

  useEffect(() => {
    fetchOrderDetails(false);

    socket.emit('join-room', oid);
    socket.on('connect',  () => console.log('socket connected', socket.id));
    socket.on('welcome',  msg => console.log(msg));
    socket.on('recieve-order-status-change', (status) => {
      if (prevDeliveryRef.current !== status) {
        playStatusSound('delivery');
        setFlashKey(k => k + 1);
      }
      prevDeliveryRef.current = status;
      setDeliveryStatus(status);
    });

    pollRef.current = setInterval(() => fetchOrderDetails(true), 3000);

    return () => {
      clearInterval(pollRef.current);
      socket.disconnect();
    };
  }, [oid]); // eslint-disable-line

  const insertPayment = async (tId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${host}/payment/insertPayment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ transaction_id: tId, resId: restaurantId }),
      });
    } catch (err) { console.error('insertPayment:', err); }
  };

  const doPayment = async (sessionid, tId) => {
    const cf = cashfreeRef.current;
    if (!cf) return;
    cf.checkout({ paymentSessionId: sessionid, redirectTarget: '_modal' }).then(result => {
      if (result.paymentDetails) {
        insertPayment(tId);
        playStatusSound('payment');
        fetchOrderDetails(false);
      }
    });
  };

  const handlePayOnline = async () => {
    if (!order || !restaurantId) return;
    setPayLoading(true);
    const tId = `${order._id}_${Date.now()}`;
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${host}/payment/createOrder`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ amount: order.totalPrice, foodOrderId: tId, cafeid: restaurantId }),
      });
      if (res.ok) {
        const data = await res.json();
        doPayment(data.payment_session_id, tId);
      } else {
        alert('Payment failed, please try again');
      }
    } catch (err) { console.error('createOrder:', err); }
    finally { setPayLoading(false); }
  };

  /* ── Loading / error states ── */
  if (loading) {
    return (
      <div className="os-root">
        <div className="os-topbar"><div className="os-topbar-logo">Your Order</div></div>
        <div className="os-skeleton-wrap">
          <div className="os-skeleton os-sk-1" />
          <div className="os-skeleton os-sk-2" />
          <div className="os-skeleton os-sk-3" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="os-root">
        <div className="os-topbar"><div className="os-topbar-logo">Your Order</div></div>
        <div className="os-empty">
          <AlertTriangle size={18} />
          Order not found
        </div>
      </div>
    );
  }

  const payMeta      = PAYMENT_META[paymentStatus] || PAYMENT_META.Pending;
  const PayIcon      = payMeta.Icon;
  const isPaid       = paymentStatus === 'Completed';
  const isTerminal   = TERMINAL_STATUSES.includes(deliveryStatus);
  const isRejected   = deliveryStatus === 'Rejected';
  const isCancelled  = deliveryStatus === 'Cancelled';
  const isOnHold     = deliveryStatus === 'Hold';
  // Block payment for rejected/cancelled orders; allow for hold
  const blockPayment = isRejected || isCancelled;
  const isFullyDone  = isPaid && deliveryStatus === 'Completed';
  const total        = safeNum(order.totalPrice);

  return (
    <div className="os-root">

      <div className="os-topbar">
        <div className="os-topbar-logo">

          <div className="os-cafe-header">
            <h1 className="os-cafe-name">{safeStr(order.cafe?.name, 'Restaurant')}</h1>
            <div className="os-cafe-tags">
              {order.cafe?.menuType === 'veg' && (
                <span className="os-cafe-badge os-badge-veg"><Leaf size={10} />Pure Veg</span>
              )}
              <span className="os-cafe-badge">
                <Link to={`/moredetails?cafeid=${restaurantId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <Store size={12} /> Visit Restaurant
                </Link>
              </span>
              
              <span className="os-cafe-badge">
                <Link to={`/moredetails/orderpage/${restaurantId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  <Store size={12} /> Back To Menu
                </Link>
              </span>
            </div>
          </div>


        </div>
        
        <div className="os-topbar-orderno">
          #{safeStr(order.dailyOrderNumber || order._id?.slice(-4), '—')}
        </div>
      </div>

      <div className="os-scroll-area">

        {/* Status flash */}
        <div className="os-flash-wrap">
          <StatusFlash deliveryStatus={deliveryStatus} animKey={flashKey} />
        </div>

        {/* Progress stepper / off-path banner */}
        <div className="os-card">
          <p className="os-card-label">Order Progress</p>
          <StatusStepper deliveryStatus={deliveryStatus} />

          {/* Hold sub-note inside the card */}
          {isOnHold && (
            <p className="os-hold-note">
              <PauseCircle size={14} />
              Your order has been paused. No action needed — we'll notify you when it resumes.
            </p>
          )}
        </div>

        {/* Ad slot — only while the order is still in progress */}
        {true && <AdSlot ads={order.cafe?.promotions} />}

        {/* Payment card — hide for fully rejected orders. Kept minimal: status + action only, no breakdown */}
        {!isRejected && (
          <div className="os-card">
            <p className="os-card-label">Payment</p>
            <div
              className="os-pay-status-badge"
              style={{ color: payMeta.color, background: payMeta.bg, border: `1px solid ${payMeta.border}` }}
            >
              <PayIcon size={13} />
              {payMeta.label}
            </div>

            {/* Online pay button — not for cancelled/rejected, not if already paid */}
            {!isPaid && !blockPayment && (
              <button className="os-pay-btn" onClick={handlePayOnline} disabled={payLoading}>
                {payLoading
                  ? <span className="os-btn-spinner" />
                  : <><CreditCard size={15} />Pay Online</>
                }
              </button>
            )}

            {/* Invoice — only when fully paid AND delivered */}
            {isFullyDone && (
              <InvoiceDownloadBtn order={order} />
            )}
          </div>
        )}

        {/* Items list — names and quantities only, no pricing */}
       {order.items && order.items.length > 0 && (
          <div className="os-card">
            <p className="os-card-label">Your Items</p>
            <div className="os-items-list">
              {order.items.map((item, idx) => (
                <div key={idx} className="os-item-row">
                  <span className="os-item-dot" />
                  <span className="os-item-name">{safeStr(item.item)}</span>
                  <span className="os-item-qty">×{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Combos — separate card since a combo line is a bundle of
            dishes at one price, not a single dish + quantity like items[] */}
        {order.combos && order.combos.length > 0 && (
          <div className="os-card">
            <p className="os-card-label">
              <Sparkles size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />
              Your Combos
            </p>
            <div className="os-combo-list">
              {order.combos.map((combo, idx) => (
                <div key={idx} className="os-combo-block">
                  <div className="os-combo-block-hd">
                    <span className="os-combo-block-name">{safeStr(combo.name)}</span>
                    <span className="os-combo-block-qty">×{combo.quantity}</span>
                  </div>
                  <div className="os-combo-block-dishes">
                    {(combo.dishes || []).map((d, di) => (
                      <div key={di} className="os-item-row os-combo-dish-row">
                        <span className="os-item-dot" />
                        <span className="os-item-name">
                          {safeStr(d.name)}{d.size ? ` (${d.size.charAt(0).toUpperCase()}${d.size.slice(1)})` : ''}
                        </span>
                        <span className="os-item-qty">×{d.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Special instructions — moved out of the items card since it's
            order-level, not tied to items[] specifically */}
        {order.specialInstructions && (
          <div className="os-card">
            <div className="os-instructions">
              <StickyNote size={14} />
              <span>{order.specialInstructions}</span>
            </div>
          </div>
        )}

        {/* Order details */}
        <div className="os-card">
          <p className="os-card-label">Order Details</p>

          <div className="os-meta-row">
            <span className="os-meta-icon"><Store size={17} /></span>
            <div>
              <p className="os-meta-label">Restaurant</p>
              <p className="os-meta-value">{safeStr(order.cafe?.name, 'Restaurant')}</p>
            </div>
          </div>

          <div className="os-meta-row">
            <span className="os-meta-icon"><Clock size={17} /></span>
            <div>
              <p className="os-meta-label">Placed At</p>
              <p className="os-meta-value">{order.placedAt ? formatDateTime(order.placedAt) : '—'}</p>
            </div>
          </div>

          {order.completedAt && (
            <div className="os-meta-row">
              <span className="os-meta-icon"><CheckCircle2 size={17} /></span>
              <div>
                <p className="os-meta-label">Completed At</p>
                <p className="os-meta-value">{formatDateTime(order.completedAt)}</p>
              </div>
            </div>
          )}

          <div className="os-meta-row">
            <span className="os-meta-icon"><Smartphone size={17} /></span>
            <div>
              <p className="os-meta-label">Contact</p>
              <p className="os-meta-value">
                {safeStr(order.user?.phone, safeStr(order.user?.email, '—'))}
              </p>
            </div>
          </div>
        </div>

        {/* Only show poll indicator for non-terminal orders */}
        {!isTerminal && (
          <div className="os-poll-indicator">
            <span className="os-poll-dot" />
            Auto-refreshing every 3 seconds
          </div>
        )}

      </div>
    </div>
  );
};

export default OrderStatus;